import React, { useCallback, useContext, useRef, useState } from 'react'
import {
  FlatList,
  Image,
  ImageSourcePropType,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  BUTTON_TYPE,
  CenterModal,
  COLORS,
  COMMON_STYLES,
  DefaultProductImage,
  FilterIcon,
  LazyImage,
  RightArrowIcon,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { rnNavigation } from 'app/utils/appConfig'
import FilterModal, {
  FORM_INPUT_TYPE,
} from 'app/farmer360/image-diagnosis/components/filter/FilterModal'
import FilterButton from 'app/components/priceRequest/FilterButton'
import { ROUTES } from 'app/routes/types'
import useFetchProducts from 'app/hooks/useFetchProducts'
import { useLoadMoreOnEndReached } from 'app/hooks/useLoadMoreOnEndReached'
import { ProductCatalogListing } from 'app/models/RequestFlow'
import SearchInput from 'app/components/reusable/SearchInput'
import useDebounce from 'app/hooks/useDebounce'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import createPriceChangeRequest from 'app/apis/createPriceChangeRequest'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { isMobileDevice } from 'app/utils/common'
import RequestSuccess from 'app/icons/RequestSuccess'
import { useSelector } from 'react-redux'
import { selectVendorId } from 'app/store/reducer/vendor'
import { AxiosError } from 'axios'
import { selectRoleType } from 'app/store/reducer/requestflow'
import ChangePriceDrawer from 'app/components/priceRequest/ChangePriceDrawer'
import { handleSuccessReset } from './RequestSuccessScreen'

const ChangePriceScreen = () => {
  const filterList = [
    {
      filterName: 'Request Status',
      filterJsonKey: 'requestStatus',
      inputType: FORM_INPUT_TYPE.MULTI_SELECT_DROPDOWN,
      filterData: [
        { id: 'pending', value: 'Pending' },
        { id: 'approved', value: 'Approved' },
        { id: 'rejected', value: 'Rejected' },
      ],
    },
    {
      filterName: 'Date Range',
      filterJsonKey: 'dateRange',
      inputType: FORM_INPUT_TYPE.MULTI_SELECT_DROPDOWN,
      filterData: [
        { id: 'today', value: 'Today' },
        { id: 'last7', value: 'Last 7 Days' },
        { id: 'last30', value: 'Last 30 Days' },
        { id: 'last365', value: 'Last 365 Days' },
        { id: 'custom', value: 'Custom Range' },
      ],
    },
  ]
  const [showDrawer, setShowDrawer] = useState(false)
  const [selectedProduct, setSelectedProduct] =
    useState<ProductCatalogListing | null>(null)
  const [newPrice, setNewPrice] = useState<string>('')
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [showSucess, setShowSucess] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery)
  const listRef = useRef<FlatList<ProductCatalogListing>>(null)
  const isDesktop = !isMobileDevice()
  const { mediaStyles } = useContext(ResponsiveContext)
  const vendorId = useSelector(selectVendorId)
  const roleType = useSelector(selectRoleType)
  const { fetchNext, request, loading, loadingNext, hasMore } = useFetchProducts({
    vendor_id: vendorId, // vendor_id
    // 'PRICE_CHANGE', // categoryid
    // 'Pending'       // brandid
    search_query: debouncedSearchQuery,
    role_type: roleType,
  })

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingNext) return
    await fetchNext()
  }, [fetchNext, hasMore, loadingNext])

  const { onEndReached, onMomentumScrollBegin, onScroll } = useLoadMoreOnEndReached(
    loadMore,
    {
      enabled: hasMore && !loading,
      isLoading: loadingNext,
      listRef,
      resetKey: debouncedSearchQuery,
    },
  )

  type AppliedFilters = {
    requestStatus?: string[]
    dateRange?: string[]
  }
  const handleApplyFilters = (filters: any) => {
    console.log('Applied Filters:', filters)
    setAppliedFilters(filters)
    setShowFilterModal(false)
  }

  const getAppliedFilters = () => {
    const filters: string[] = []

    if (appliedFilters?.requestStatus?.length) {
      filters.push(...appliedFilters.requestStatus)
    }

    if (appliedFilters?.dateRange?.length) {
      filters.push(...appliedFilters.dateRange)
    }

    return filters
  }

  const handleClearFilter = (filterToRemove?: string) => {
    if (!filterToRemove) {
      setAppliedFilters({})
      return
    }
    const updated = {
      requestStatus: appliedFilters.requestStatus?.filter(
        f => f !== filterToRemove,
      ),
      dateRange: appliedFilters.dateRange?.filter(f => f !== filterToRemove),
    }
    setAppliedFilters(updated)
  }
  const handleSubmit = async (item: ProductCatalogListing ,reason: string) => {
    try {
      const res = await createPriceChangeRequest(
        vendorId,
        item.product_variant_id,
        item.online_price,
        newPrice,
        'Store,Node',
        roleType,
        reason,
      )
      if ((res as AxiosError)?.response?.status === 400) {
        const err = res as AxiosError<any>
        ToastAndroid.show(err.response?.data.detail, ToastAndroid.SHORT)
        setShowDrawer(false)
      } else {
        setShowDrawer(false)
        if (isDesktop) {
          setShowSucess(true)
        } else {
          rnNavigation({ name: ROUTES.REQUEST_SUCCESS })
        }
      }
      setNewPrice('')
    } catch (e) {
      console.log('Error submitting request', e)
    }
  }
  const renderItem = ({ item }: { item: ProductCatalogListing }) => {
    return (
      <Pressable
        style={styles.card}
        onPress={() => {
          setSelectedProduct(item)
          setShowDrawer(true)
        }}
      >
        <LazyImage
          imageUrl={item.product_image}
          fallBackIcon={
            <Image
              source={DefaultProductImage as ImageSourcePropType}
              style={styles.productImage}
            />
          }
          imgStyles={styles.productImage}
        />
        <View style={[COMMON_STYLES.flex1, { rowGap: 2 }]}>
          <Text style={styles.brand}>{item.brand}</Text>
          <Text style={styles.name}>{item.product_name}</Text>
          <Text style={styles.name}>{item.attributes_name}</Text>
        </View>
        <View style={styles.changebtn}>
          <Text style={styles.ctaText}>Change Price</Text>
          <RightArrowIcon
            strokeColor={'white'}
            width={20}
            style={[
              COMMON_STYLES.justifyCenter,
              COMMON_STYLES.alignItemsCenter,
              BG_STYLES.bgPrimary100,
              {
                paddingBottom: 4,
                paddingHorizontal: 2,
              },
              COMMON_STYLES.br100,
            ]}
          />
        </View>
      </Pressable>
    )
  }
  if (loading && request.length === 0) return <Spinner />
  return (
    <>
      <View style={{ padding: 8, gap: 5 }}>
        <SearchInput
          placeholder="Search for a product, brand"
          value={searchQuery}
          onChange={setSearchQuery}
          inputContainerStyle={[
            mediaStyles({
              desktopStyle: styles.desktop,
            }),
          ]}
        />
        <ScrollView
          style={[
            COMMON_STYLES.row,
            COMMON_STYLES['pt1/4'],
            COMMON_STYLES['ph1/2'],
            mediaStyles({
              desktopStyle: styles.desktop,
            }),
          ]}
          horizontal
          showsHorizontalScrollIndicator={false}
        >
          {/* <Pressable
            style={styles.filterBtn}
            onPress={() => setShowFilterModal(true)}
          >
            <FilterIcon
              svgProps={{
                height: 22,
                width: 22,
                fill: COLORS.neutral70,
              }}
            />
          </Pressable> */}
          <FilterButton
            appliedFilters={getAppliedFilters()}
            onClearFilter={handleClearFilter}
          />
        </ScrollView>
      </View>
      <View style={{ paddingTop: 4, flex: 1 }}>
        <FlatList
          ref={listRef}
          data={request}
          keyExtractor={item => item.id.toString()}
          renderItem={renderItem}
          showsVerticalScrollIndicator={false}
          onScroll={onScroll}
          scrollEventThrottle={16}
          onMomentumScrollBegin={onMomentumScrollBegin}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.2}
          ListFooterComponent={
            loadingNext ? (
              <View style={styles.footerLoader}>
                <Spinner type="in-screen" />
              </View>
            ) : null
          }
          contentContainerStyle={[
            styles.listContainer,
            mediaStyles({
              desktopStyle: styles.desktop,
            }),
          ]}
        />
      </View>
      {/* {showFilterModal && (
        <FilterModal
          filterList={filterList}
          appliedFilterData={appliedFilters}
          onApplyFilters={handleApplyFilters}
          onClose={() => setShowFilterModal(false)}
        />
      )} */}
      {showSucess && (
        <CenterModal
          isVisible={showSucess}
          onClose={() => {
            setShowSucess(false)
          }}
          style={[BG_STYLES.bgSuccess100]}
        >
          <View style={styles.content}>
            <RequestSuccess />
            <Text style={styles.text}>
              Price Change Request{'\n'}Submitted!
            </Text>
          </View>
          <View style={styles.footer}>
            <Button
              buttonStyle={[COMMON_STYLES.mb1, BG_STYLES.bgPrimary10]}
              onClick={() => {
                setShowSucess(false)
                handleSuccessReset()
              }}
              type={BUTTON_TYPE.PRIMARY}
              text="View Request"
              size={SIZE.large}
              textStyle={TEXT_STYLES.textPrimary100}
            />
          </View>
        </CenterModal>
      )}
      {showDrawer && (
        <ChangePriceDrawer
          visible={showDrawer}
          onClose={() => setShowDrawer(false)}
          selectedProduct={selectedProduct}
          newPrice={newPrice}
          setNewPrice={setNewPrice}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  )
}
const styles = StyleSheet.create({
  card: {
    ...BG_STYLES.bgWhite,
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES['p3/2'],
    borderTopColor: COLORS.neutral20,
    borderTopWidth: 1,
  },
  productImage: {
    width: 64,
    height: 64,
    marginRight: 14,
  },
  brand: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
  },
  name: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
  },
  ctaText: {
    ...TEXT_STYLES.textPrimary100,
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    alignItems: 'baseline',
  },
  changebtn: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    columnGap: 8,
    alignSelf: 'flex-end',
  },
  filterBtn: {
    ...COMMON_STYLES['ph1/2'],
    ...COMMON_STYLES['pv1/2'],
    ...BORDER_STYLES.borderNeutral20,
    ...COMMON_STYLES.b1,
    ...BG_STYLES.bgWhite,
    ...COMMON_STYLES['br1/2'],
    marginRight: 12,
    width: 32,
    height: 32,
    alignItems: 'center',
  },

  desktop: {
    maxWidth: '70%',
    width: '100%',
    alignSelf: 'center',
  },
  content: {
    ...COMMON_STYLES.flex1,
    ...COMMON_STYLES.justifyCenter,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.ph1,
  },
  footer: {
    ...COMMON_STYLES.p1,
    ...COMMON_STYLES.fullWidth,
  },
  text: {
    ...TEXT_STYLES.textNeutral00,
    ...TYPOGRAPHY.HEADING3,
    ...VARIANTS_STYLES.MEDIUM,
    ...COMMON_STYLES.textCenter,
    ...COMMON_STYLES.mt1,
  },
  listContainer: {
    paddingBottom: 24,
  },
  footerLoader: {
    minHeight: 56,
    ...COMMON_STYLES.justifyCenter,
    ...COMMON_STYLES.alignItemsCenter,
  },
})

export default ChangePriceScreen
